import React, { useEffect, useState } from 'react';
import {
  Box, Button, Chip, IconButton, Stack, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';
import ThumbDownAltOutlinedIcon from '@mui/icons-material/ThumbDownAltOutlined';
import CloseIcon from '@mui/icons-material/Close';
import {
  LLM_FEEDBACK_FEATURE_TAGS, LLM_FEEDBACK_TAG_LABELS, LlmFeature, LlmRating,
} from 'model/llm-feedback';
import { readMyLlmFeedback, saveLlmFeedback } from 'utils/api-client';
import { parseAxiosErrorMessage } from 'utils/axios-error-messages';
import { profileCreationOnly } from 'utils/demo-mode';

interface Props {
  feature: LlmFeature;
  targetId: string;
  /* Snapshot of what's being rated, for features whose output isn't stored. */
  context?: Record<string, unknown>;
  title?: string;
  /* Shown under the thumbs: what gets sent along with the feedback. */
  note?: string;
  /* Thumbs only until the person clicks one (chat answers). */
  compact?: boolean;
  onDismiss?: () => void;
}

/*
 * "How was this?" for any AI output in HQ: thumbs, tags (per feature) and a
 * comment. Saved to the LLM feedback log (crud/llm_feedback.py), one entry
 * per person per output, so it can be changed. Reviewed on the LLM Feedback
 * page to tune the prompts. In compact mode a thumb saves straight away and
 * then offers tags and a comment.
 */
const LlmFeedbackWidgetVisible = ({
  feature, targetId, context, title = 'How was this?', note, compact = false, onDismiss,
}: Props) => {
  const [rating, setRating] = useState<LlmRating | null>(null);
  const [tags, setTags] = useState<string[]>([]);
  const [comment, setComment] = useState('');
  const [saved, setSaved] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(!compact);

  useEffect(() => {
    let cancelled = false;
    setRating(null);
    setTags([]);
    setComment('');
    setSaved('idle');
    setExpanded(!compact);
    readMyLlmFeedback([targetId], feature)
      .then((rows) => {
        const mine = rows[0];
        if (cancelled || !mine) return;
        setRating(mine.rating ?? null);
        setTags(mine.tags ?? []);
        setComment(mine.comment ?? '');
        setSaved('saved');
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [targetId, feature, compact]);

  const send = async (next?: { rating?: LlmRating | null }) => {
    const sendRating = next && 'rating' in next ? next.rating : rating;
    setSaved('saving');
    setError(null);
    try {
      await saveLlmFeedback({
        feature, target_id: targetId, rating: sendRating, tags, comment: comment.trim(), context,
      });
      setSaved('saved');
    } catch (e) {
      setSaved('idle');
      setError(parseAxiosErrorMessage(e));
    }
  };

  const onRate = (value: LlmRating | null) => {
    setRating(value);
    setSaved('idle');
    if (compact && value) {
      setExpanded(true);
      send({ rating: value });
    }
  };

  const toggleTag = (tag: string) => {
    setSaved('idle');
    setTags((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]));
  };

  const hasSomething = !!rating || tags.length > 0 || !!comment.trim();

  const thumbs = (
    <ToggleButtonGroup size="small" exclusive value={rating} onChange={(_e, value: LlmRating | null) => onRate(value)}>
      <ToggleButton value="up" aria-label="Good" sx={compact ? { p: 0.25 } : undefined}>
        <ThumbUpAltOutlinedIcon fontSize="small" />
      </ToggleButton>
      <ToggleButton value="down" aria-label="Not good" sx={compact ? { p: 0.25 } : undefined}>
        <ThumbDownAltOutlinedIcon fontSize="small" />
      </ToggleButton>
    </ToggleButtonGroup>
  );

  if (compact && !expanded) {
    return (
      <Tooltip title={note || 'Rate this'}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ display: 'inline-flex', mt: 0.5 }}>
          {title && <Typography variant="caption" color="text.secondary">{title}</Typography>}
          {thumbs}
          {onDismiss && (
            <IconButton size="small" onClick={onDismiss} aria-label="Close"><CloseIcon fontSize="small" /></IconButton>
          )}
        </Stack>
      </Tooltip>
    );
  }

  return (
    <Box sx={{
      border: 1, borderColor: 'divider', borderRadius: 1, p: compact ? 1 : 1.5, mt: compact ? 0.5 : 0,
    }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{title || 'How was this?'}</Typography>
        {thumbs}
        {!compact && (
          <Typography variant="caption" color="text.secondary">
            Goes to whoever tunes HQ&apos;s AI — tell them what to change.
          </Typography>
        )}
        {onDismiss && (
          <IconButton size="small" onClick={onDismiss} aria-label="Close" sx={{ ml: 'auto' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Stack>
      <Stack direction="row" gap={0.75} flexWrap="wrap" sx={{ mt: 1 }}>
        {LLM_FEEDBACK_FEATURE_TAGS[feature].map((tag) => {
          const on = tags.includes(tag);
          return (
            <Chip
              key={tag}
              size="small"
              label={LLM_FEEDBACK_TAG_LABELS[tag] ?? tag}
              clickable
              onClick={() => toggleTag(tag)}
              color={on ? 'primary' : 'default'}
              variant={on ? 'filled' : 'outlined'}
              sx={on ? undefined : { color: 'text.primary', borderColor: 'grey.500' }}
            />
          );
        })}
      </Stack>
      <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mt: 1 }}>
        <TextField
          size="small"
          fullWidth
          multiline
          maxRows={4}
          label="Comment (optional)"
          placeholder="What was wrong or right?"
          value={comment}
          onChange={(e) => { setComment(e.target.value); setSaved('idle'); }}
          inputProps={{ maxLength: 3000 }}
        />
        <Button
          variant="outlined"
          onClick={() => send()}
          disabled={!hasSomething || saved === 'saving' || saved === 'saved'}
          sx={{ whiteSpace: 'nowrap' }}
        >
          {saved === 'saved' ? 'Feedback saved' : 'Send feedback'}
        </Button>
      </Stack>
      {note && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>{note}</Typography>}
      {error && <Typography variant="caption" color="error">{error}</Typography>}
    </Box>
  );
};

// Hidden in demo mode / builds without profile creation (utils/demo-mode.ts).
export const LlmFeedbackWidget = profileCreationOnly(LlmFeedbackWidgetVisible);
